import { useEffect, useState } from 'preact/hooks';
import {
  approveAsk,
  declineAsk,
  getRecordWithLinks,
  listAsks,
  listTeamMembers,
  ApiError,
  authorityOfferFrom,
  type Ask,
  type LinkRow,
  type RecordRow,
  type TeamMember,
} from '../lib/api';
import { askActionLabel, askOutcome, askTitle, doorAskSentence } from '../lib/asks';
import { resolveRecordDestination, type AttentionDestination } from '../lib/attentionRoute';
import { routePath, type AskInboxView } from '../routes/route';
import { bumpRecordsVersion, openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './Asks.css';

// S11 — THE ASKS INBOX: the room the authority model always routed to, and
// never gave a door.
//
// The promise is "a change beyond your authority does not fail, it converts
// into a request" (decision 08547e14). Before this screen the request
// converted and then disappeared: the server has had the whole ask API
// since C5b, and packages/web called exactly one of its five routes — the
// POST that files one. Alan could only answer with curl, and the person who
// asked had no way to learn what happened.
//
// So this screen is two halves, and the second is not the lesser one:
//   • WAITING ON YOU (routed_to_me) — what you must answer. Approve, or
//     decline WITH A REASON. The reason is not garnish: a decline the asker
//     never sees is exactly the invisible outcome this product exists to
//     avoid, and the server refuses a reasonless decline anyway.
//   • YOU ASKED (filed_by_me) — "did my request get answered?" S4's
//     dispatcher who pressed "Request a change" had nowhere to learn the
//     answer. Receipts, not acknowledgements.
//
// It is NOT a rail destination — the blessed rails (wf02 §01b) carry no
// Asks tab and this chunk does not change blessed nav. It is reached from
// Today's "Needs you" (where an ask routed to you now appears, folded into
// the ONE attention list) and from the receipt a filed ask leaves on the
// screen you filed it from.
//
// Every row is a doorway to the record the ask concerns, resolved through
// lib/attentionRoute's one table — the same one Today's tiles use.
//
// ---------------------------------------------------------------------
// S16 — THE ONE FAN-OUT THAT COULD NOT BE BATCHED, AND EXACTLY WHY.
//
// S16 converted every other per-record link loop in the app to S15's bulk
// read (`GET /records?type_key=…&include=links`, one request per page of
// records). This screen cannot use it, and the reason is structural rather
// than an oversight:
//
//   • an ask carries `targetId` and NOT a target type — core's ask row has
//     no `target_type` column and `serializeAsk` therefore has none to
//     send (packages/server/src/plugins/asks.ts);
//   • `GET /records` REQUIRES `type_key` (its listQuerySchema), and there
//     is no read-many-records-by-id door on the records API at all.
//
// So the only way to learn what an ask is about is still one read per
// subject. Batching by type instead would mean listing every doorway-
// capable Rexx type (thirteen of them) on the chance a subject is in one —
// more requests than the fan-out for any inbox a person could answer, and
// it would pull hundreds of unrelated rows to name three. Guessing the type
// from `actionId` ('rexx.pay_basis.change' → hvac_pay_profile) is exactly
// the inference this tranche exists to delete: the manifest declares no
// action→type mapping the client can read, so it would be a guess wearing a
// lookup's clothes.
//
// The honest closing move is a one-line server change — serialize the
// ask's target TYPE beside its id, and this screen becomes two grouped
// bulk reads — and that is named in the chunk report rather than smuggled
// in here. Meanwhile the cost is bounded by SUBJECT_JOIN_CAP and, for the
// first time, the cap SAYS SO on the rows it bites: a subject past it now
// reads "subject not read" instead of quietly rendering as an ask about
// nothing.
//
// What this chunk did land here: the screen's own failure now speaks the
// product's vocabulary (lib/readFailure) — a refused or rate-limited inbox
// says which refusal it was and offers to read again, instead of the
// 2026-08-13 shape where a 429 told the office nobody had asked for
// anything.

export interface AsksProps {
  tenantId: string;
  view: AskInboxView;
  /** The ask a doorway arrived on — opened, so "the line I tapped" and
   * "the ask I am answering" are the same thing. */
  focusId?: string;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

/** Past this many distinct subjects the per-ask record read stops being a
 * handful of requests. This is the LAST rationing cap in the Rexx app and
 * it survives S16 only because the bulk read cannot reach here (see the
 * header): with no target type on the ask and no read-many-by-id door,
 * there is nothing to batch, so an unbounded walk is the alternative and
 * that is how a screen eats a rate limit. It is bounded, and — S16 — the
 * rows it bites now say they went unread instead of rendering as asks about
 * nothing. An inbox this size is not a UI problem any more. */
const SUBJECT_JOIN_CAP = 40;

function relativeWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Who asked, in the words a family uses. A member with no name set (a
 *  child's seat, invited by address and never asked for one) used to read as
 *  their raw email address on every ask (2026-09-29 live defect). The
 *  mailbox part, without any `+tag`, is the name the family chose when they
 *  made the address — `glee@…` reads "Glee". */
function nameOf(member: TeamMember | undefined): string | null {
  if (!member) return null;
  const named = member.name?.trim();
  if (named) return named;
  const local = (member.email ?? '').split('@')[0]?.split('+')[0]?.replace(/[._-]+/g, ' ').trim();
  if (!local) return null;
  return local.replace(/\b\w/g, (c) => c.toUpperCase());
}

interface Notice {
  tone: 'good' | 'bad';
  text: string;
}

/** The record an ask is about, with the links the doorway may have to walk. */
interface Subject {
  record: RecordRow;
  links: LinkRow[];
}

export function Asks({ tenantId, view, focusId, onNavigate }: AsksProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [routed, setRouted] = useState<Ask[]>([]);
  const [filed, setFiled] = useState<Ask[]>([]);
  const [subjects, setSubjects] = useState<Map<string, Subject>>(new Map());
  /** Subject ids the cap refused to look up. Their rows say so — the
   *  difference between "this ask is about a record we could not name" and
   *  "this ask is about nothing" is the whole of this screen's honesty. */
  const [beyondCap, setBeyondCap] = useState<Set<string>>(new Set());
  const [people, setPeople] = useState<Map<string, TeamMember>>(new Map());
  const [open, setOpen] = useState<string | null>(focusId ?? null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [reload, setReload] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    setOpen(focusId ?? null);
  }, [focusId]);

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      // Only the FIRST load is a "one moment" — a reload after an answer
      // keeps the settled screen (and its receipt) on the glass instead of
      // blanking the very outcome the person just produced.
      setLoad((current) => (current === 'ready' ? current : 'loading'));
      setFailure(null);
      try {
        const [routedAsks, filedAsks] = await Promise.all([
          // The server defaults this lens to status 'open' — a queue is
          // unanswered work by definition.
          listAsks(tenantId, 'routed_to_me'),
          // And this one to every status, which is the whole point of it.
          listAsks(tenantId, 'filed_by_me'),
        ]);
        if (cancelled) return;

        // WHO ASKED. The roster route is admin-only, so a holder who is not
        // an admin simply gets no names — an expected outcome, not a broken
        // screen: the row still says what is being asked and about what.
        const roster = await listTeamMembers(tenantId).catch(() => [] as TeamMember[]);

        // WHAT IT IS ABOUT. The ask carries its subject's id but not its
        // type, so the record itself is the only place the doorway can come
        // from. Bounded by the cap above; a subject this actor may not read
        // answers 404-as-absence by design (asks.ts's oracle rule), which
        // costs the row its doorway and nothing else.
        const wanted = [
          ...new Set(
            [...routedAsks, ...filedAsks]
              .map((a) => a.targetId)
              .filter((id): id is string => typeof id === 'string' && id.length > 0)
          ),
        ];
        const ids = wanted.slice(0, SUBJECT_JOIN_CAP);
        const fetched = await Promise.all(
          ids.map((id) => getRecordWithLinks(tenantId, id).catch(() => null))
        );
        if (cancelled) return;

        const subjectMap = new Map<string, Subject>();
        for (const got of fetched) if (got) subjectMap.set(got.record.id, got);
        const personMap = new Map<string, TeamMember>();
        for (const member of roster) personMap.set(member.userId, member);

        setRouted(routedAsks);
        setFiled(filedAsks);
        setSubjects(subjectMap);
        setBeyondCap(new Set(wanted.slice(SUBJECT_JOIN_CAP)));
        setPeople(personMap);
        setLoad('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
      }
    }
    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, reload]);

  function actionFor(dest: AttentionDestination): () => void {
    return dest.kind === 'editor'
      ? () => openRecordEditor({ mode: 'edit', typeKey: dest.typeKey, id: dest.id })
      : () => onNavigate(dest.path);
  }

  /**
   * The record an ask is about — always NAMED, and opened where the one
   * doorway table says that kind of record is acted on.
   *
   * The second hop is not a flourish. Some record types deliberately have
   * no door of their own: hvac_pay_profile is the live example — S8 split
   * pay onto its own type and S10 put its editor on the TECH screen, so the
   * pay record is edited THROUGH the tech and the doorway table names no
   * destination for it (adding one would mint a second pay-editing surface,
   * which S11 is explicitly not allowed to do). Those records always hang
   * off one that does have a screen, so when the type resolves to nothing
   * we follow its links to the first record that has a screen — generic,
   * no per-type special case, and it is why the subject read asks for links.
   *
   * A subject we cannot name at all (the actor may not read it — 404 as
   * absence, by design) simply isn't here; the row still says what is being
   * asked and stays answerable.
   */
  function subjectOf(ask: Ask): { record: RecordRow; act: (() => void) | null } | null {
    const subject = ask.targetId ? subjects.get(ask.targetId) : undefined;
    if (!subject) return null;
    const direct = resolveRecordDestination(subject.record.record_type, subject.record.id, 'rexx');
    if (direct) return { record: subject.record, act: actionFor(direct) };
    for (const link of subject.links) {
      const parent = resolveRecordDestination(link.dst_type, link.dst_id, 'rexx');
      if (parent && parent.kind === 'navigate') {
        return { record: subject.record, act: actionFor(parent) };
      }
    }
    return { record: subject.record, act: null };
  }

  function refresh() {
    setDeclining(false);
    setReason('');
    setReload((n) => n + 1);
  }

  /** THE ANSWER. Approving re-executes the carried remainder as the
   * approver, so the underlying change actually lands — which is why this
   * bumps the records version: every other read surface in the app is now
   * stale. A refusal is rendered as the server said it, never swallowed and
   * never a button that pretended. */
  async function doApprove(ask: Ask) {
    setBusy(true);
    setNotice(null);
    try {
      const result = await approveAsk(tenantId, ask.id);
      if (result.kind === 'declined') {
        setNotice({ tone: 'bad', text: result.detail });
      } else {
        const subject = subjectOf(ask);
        setNotice({
          tone: 'good',
          text: subject
            ? `Approved — ${askActionLabel(ask).toLowerCase()} is done on ${subject.record.display_name}.`
            : `Approved — ${askActionLabel(ask).toLowerCase()} is done.`,
        });
        bumpRecordsVersion();
      }
      refresh();
    } catch (err) {
      const offer = authorityOfferFrom(err);
      setNotice({
        tone: 'bad',
        text: offer
          ? `Approving this is beyond your own authority — it needs ${offer.routeTo ?? 'someone else'}.`
          : err instanceof ApiError
            ? err.message
            : 'That didn’t go through. Try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  async function doDecline(ask: Ask) {
    const words = reason.trim();
    if (!words) return;
    setBusy(true);
    setNotice(null);
    try {
      await declineAsk(tenantId, ask.id, words);
      const asker = nameOf(ask.requesterUserId ? people.get(ask.requesterUserId) : undefined);
      setNotice({
        tone: 'good',
        text: asker
          ? `Declined — ${asker} will see your reason.`
          : 'Declined — your reason is on the request the asker reads.',
      });
      refresh();
    } catch (err) {
      setNotice({
        tone: 'bad',
        text: err instanceof ApiError ? err.message : 'That didn’t go through. Try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  const crumb = (
    <nav class="asks__crumb" aria-label="Breadcrumb">
      <button type="button" onClick={() => onNavigate(routePath({ screen: 'today' }))}>
        Today
      </button>
      <span class="asks__crumb-sep" aria-hidden="true">
        ›
      </span>
      <span class="asks__crumb-cur">Asks</span>
    </nav>
  );

  if (load === 'loading') {
    return (
      <div class="asks">
        {crumb}
        <div class="asks__eyebrow">Asks</div>
        <div class="asks__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    // NOT "nothing is waiting on your say-so". On 2026-08-13 a rate limit
    // told the office exactly that; a refused inbox now says which refusal
    // it was and offers to read again.
    return (
      <div class="asks">
        {crumb}
        <div class="asks__eyebrow">Asks</div>
        <div class="asks__say">
          {failure.headline}
          <br />
          <span class="asks__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="asks__retry" onClick={() => setReload((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  const rows = view === 'routed' ? routed : filed;

  return (
    <div class="asks">
      {crumb}
      <div class="asks__eyebrow">Asks</div>
      <h2 class="asks__title">
        {view === 'routed'
          ? routed.length === 1
            ? '1 waiting on you'
            : `${routed.length} waiting on you`
          : filed.length === 1
            ? '1 you asked for'
            : `${filed.length} you asked for`}
      </h2>

      <div class="asks__lenses" role="group" aria-label="Which asks">
        <button
          type="button"
          class={view === 'routed' ? 'asks__pill asks__pill--on' : 'asks__pill'}
          onClick={() => onNavigate(routePath({ screen: 'asks', view: 'routed' }))}
        >
          Waiting on you<b class="asks__pill-n">{routed.length}</b>
        </button>
        <button
          type="button"
          class={view === 'filed' ? 'asks__pill asks__pill--on' : 'asks__pill'}
          onClick={() => onNavigate(routePath({ screen: 'asks', view: 'filed' }))}
        >
          You asked<b class="asks__pill-n">{filed.length}</b>
        </button>
      </div>

      {notice ? (
        <div
          class={notice.tone === 'good' ? 'asks__notice asks__notice--good' : 'asks__notice'}
          role="status"
        >
          {notice.text}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div class="asks__empty">
          {view === 'routed'
            ? 'Nothing is waiting on your say-so.'
            : 'You haven’t asked anyone for anything.'}
        </div>
      ) : (
        <div class="asks__list">
          {rows.map((ask) => {
            const outcome = askOutcome(ask);
            const subject = subjectOf(ask);
            // S16: three different truths where there used to be two. A
            // subject the cap never looked up is NOT a subject that isn't
            // there, and neither is one this actor may not read (asks.ts's
            // 404-as-absence rule) — but only the first is our doing, and
            // only the first is worth saying out loud.
            const unlooked = ask.targetId !== null && beyondCap.has(ask.targetId);
            const asker = nameOf(ask.requesterUserId ? people.get(ask.requesterUserId) : undefined);
            const sub = [
              view === 'filed' ? null : asker,
              subject?.record.display_name ?? (unlooked ? 'subject not read' : null),
            ]
              .filter(Boolean)
              .join(' · ');
            const isOpen = open === ask.id;
            return (
              <div class="asks__item" key={ask.id}>
                <button
                  type="button"
                  class="asks__row"
                  aria-expanded={isOpen}
                  onClick={() => {
                    setDeclining(false);
                    setReason('');
                    setNotice(null);
                    setOpen(isOpen ? null : ask.id);
                  }}
                >
                  <span class={`asks__stripe asks__stripe--${outcome.tone}`} />
                  <span class="asks__main">
                    <b>{askActionLabel(ask)}</b>
                    <small>{sub || relativeWhen(ask.createdAt)}</small>
                  </span>
                  <span class="asks__right">
                    <span class={`asks__state asks__state--${outcome.tone}`}>{outcome.label}</span>
                    <span class="asks__when">{relativeWhen(ask.createdAt)}</span>
                  </span>
                </button>

                {isOpen ? (
                  <div class="asks__panel">
                    {/* G45: a door ask's own sentence already states what
                        approving does AND what it costs to undo (bullet 5:
                        "Approving sends the letter to Nikki by email · Can
                        be withdrawn until it is sent") — one machine
                        sentence, not an instructional caption layered on
                        top of the title. */}
                    <div class="asks__what">{doorAskSentence(ask) ?? askTitle(ask)}</div>
                    {outcome.detail ? <div class="asks__reason">{outcome.detail}</div> : null}

                    <div class="asks__acts">
                      {subject && subject.act ? (
                        <button type="button" class="asks__btn asks__btn--ghost" onClick={subject.act}>
                          {subject.record.display_name} ›
                        </button>
                      ) : subject ? (
                        <span class="asks__subject">{subject.record.display_name}</span>
                      ) : unlooked ? (
                        <span class="asks__subject">
                          This inbox is too large to name every subject — this one was not read.
                        </span>
                      ) : null}

                      {view === 'routed' && ask.status === 'open' && !declining ? (
                        <>
                          <button
                            type="button"
                            class="asks__btn"
                            disabled={busy}
                            onClick={() => void doApprove(ask)}
                          >
                            {busy ? 'Approving…' : 'Approve'}
                          </button>
                          <button
                            type="button"
                            class="asks__btn asks__btn--ghost"
                            disabled={busy}
                            onClick={() => setDeclining(true)}
                          >
                            Decline
                          </button>
                        </>
                      ) : null}
                    </div>

                    {view === 'routed' && ask.status === 'open' && declining ? (
                      <div class="asks__decline">
                        <textarea
                          class="asks__reason-input"
                          rows={2}
                          value={reason}
                          placeholder="Why not?"
                          onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
                        />
                        <div class="asks__acts">
                          <button
                            type="button"
                            class="asks__btn"
                            disabled={busy || reason.trim().length === 0}
                            onClick={() => void doDecline(ask)}
                          >
                            {busy ? 'Sending…' : 'Send decline'}
                          </button>
                          <button
                            type="button"
                            class="asks__btn asks__btn--ghost"
                            disabled={busy}
                            onClick={() => {
                              setDeclining(false);
                              setReason('');
                            }}
                          >
                            Keep it open
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
